import {Record, List} from 'immutable';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';
import {type IClosingDay} from './types';

interface IClosingDaysState {
    fetching: boolean;
    creating: boolean;
    deleting: boolean;
    deletingIds: List<string>;
    closingDays: List<IClosingDay>;
    behaviors: BehaviorsCollection;
}

class ClosingDaysState extends Record<IClosingDaysState>({
    fetching: false,
    creating: false,
    deleting: false,
    deletingIds: List(),
    closingDays: List(),
    behaviors: new BehaviorsCollection([]),
}) {}

const getClosingDayIdsFromClosingRange = (closingDays: List<IClosingDay>) =>
    closingDays.map(closingDay => closingDay.closingDayId);

export default makeReducer<ClosingDaysState>(
    [
        match(
            Actions.fetch,
            () =>
                new ClosingDaysState({
                    fetching: true,
                }),
        ),
        match(Actions.fetchFailed, state =>
            state.merge({
                fetching: false,
            }),
        ),
        match(Actions.fetchSuccess, (state, {payload}) =>
            state.merge({
                fetching: false,
                closingDays: payload.response.closingDays,
                behaviors: payload.response.behaviors,
            }),
        ),
        match(Actions.addClosingDays, state =>
            state.merge({
                creating: true,
            }),
        ),
        match(Actions.addClosingDaysFailed, state =>
            state.merge({
                creating: false,
            }),
        ),
        match(Actions.addClosingDaysSuccess, (state, {payload}) =>
            state.merge({
                creating: false,
                closingDays: state.closingDays.concat(payload.closingDays),
            }),
        ),
        match(Actions.deleteClosingDay, (state, {payload}) =>
            state.merge({
                deleting: true,
                deletingIds: state.deletingIds.push(payload.closingDayId),
            }),
        ),
        match(Actions.deleteClosingDayFailed, (state, {payload}) =>
            state.merge({
                deleting: false,
                deletingIds: state.deletingIds.filter(id => id !== payload.closingDayId),
            }),
        ),
        match(Actions.deleteClosingDaySuccess, (state, {payload}) =>
            state.merge({
                deleting: false,
                deletingIds: state.deletingIds.filter(id => id !== payload.closingDayId),
                closingDays: state.closingDays.filter(closingDay => closingDay.closingDayId !== payload.closingDayId),
            }),
        ),
        match(Actions.deleteClosingDayAborted, (state, {payload}) =>
            state.merge({
                deleting: false,
                deletingIds: state.deletingIds.filter(id => id !== payload.closingDayId),
            }),
        ),
        match(Actions.deleteClosingInterval, (state, {payload}) =>
            state.merge({
                deleting: true,
                deletingIds: state.deletingIds.concat(payload.closingDays.map(closingDay => closingDay.closingDayId)),
            }),
        ),
        match(Actions.deleteClosingIntervalFailed, (state, {payload}) => {
            const closingDayIds = getClosingDayIdsFromClosingRange(payload.closingDays);
            return state.merge({
                deleting: false,
                deletingIds: state.deletingIds.filter(id => !closingDayIds.includes(id)),
            });
        }),
        match(Actions.deleteClosingIntervalSuccess, (state, {payload}) => {
            const closingDayIds = getClosingDayIdsFromClosingRange(payload.closingDays);
            return state.merge({
                deleting: false,
                deletingIds: state.deletingIds.filter(id => !closingDayIds.includes(id)),
                closingDays: state.closingDays.filter(closingDay => !closingDayIds.includes(closingDay.closingDayId)),
            });
        }),
        match(Actions.deleteClosingIntervalAborted, (state, {payload}) => {
            const closingDayIds = getClosingDayIdsFromClosingRange(payload.closingDays);
            return state.merge({
                deleting: false,
                deletingIds: state.deletingIds.filter(id => !closingDayIds.includes(id)),
            });
        }),
    ],
    new ClosingDaysState(),
);
